import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowUpFromLine,
  ChevronDown,
  ChevronRight,
  FileText,
  GitCompare,
  History,
  PlayCircle,
  Plus,
  Save,
  Settings,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { CellLangPicker } from '@/components/CellLangPicker';
import {
  DefinitionEditor,
  checkComplete,
  emptyDefinition,
} from '@/components/DefinitionEditor';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import {
  api,
  normalizeCallType,
  type Language,
  type PersonaListItem,
  type PersonaState,
  type PromptDetail,
  type PromptListItem,
  type VariableDefinition,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { classifyVariable, extractUnknownVariables } from '@/lib/variable-classify';

export const Route = createFileRoute('/prompts')({
  component: PromptsPage,
});

function PromptsPage() {
  const [cell, setCell] = useState('EI_PL_BucketX');
  const [language, setLanguage] = useState<Language>('EN');
  const [selected, setSelected] = useState<{ name: string; version: number } | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1>Prompts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Click a row to expand; the body, variable form, and version history land below.
            Editing the body auto-bumps the version — prior versions stay listed read-only.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          <Plus className="size-4" />
          New prompt
        </button>
      </div>

      <CellLangPicker
        cell={cell}
        language={language}
        onChange={({ cell, language }) => {
          setCell(cell);
          setLanguage(language);
          setSelected(null);
        }}
      />

      <PromptList
        cell={cell}
        language={language}
        selected={selected}
        onSelect={(p) => setSelected({ name: p.name, version: p.version })}
      />

      {selected && (
        <PromptDetailPanel
          cell={cell}
          language={language}
          name={selected.name}
          version={selected.version}
          onVersionChange={(v) => setSelected({ name: selected.name, version: v })}
        />
      )}

      {creating && (
        <NewPromptModal
          cell={cell}
          language={language}
          onClose={() => setCreating(false)}
          onCreated={(detail) => {
            setCreating(false);
            setSelected({ name: detail.name, version: detail.version });
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Prompt list — grouped by name, latest version per row, click to expand
// ────────────────────────────────────────────────────────────────────

type PromptGroup = { name: string; versions: PromptListItem[] };

function groupByName(prompts: PromptListItem[]): PromptGroup[] {
  const m = new Map<string, PromptListItem[]>();
  for (const p of prompts) {
    if (!m.has(p.name)) m.set(p.name, []);
    m.get(p.name)!.push(p);
  }
  return Array.from(m.entries())
    .map(([name, versions]) => ({
      name,
      versions: versions.sort((a, b) => b.version - a.version),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function PromptList({
  cell,
  language,
  selected,
  onSelect,
}: {
  cell: string;
  language: Language;
  selected: { name: string; version: number } | null;
  onSelect: (p: PromptListItem) => void;
}) {
  const q = useQuery({
    queryKey: ['prompts', cell, language],
    queryFn: () => api.listPrompts(cell, language),
    retry: 0,
  });
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  if (q.isLoading) return <PageLoading />;
  if (q.error) return <PageError message={String(q.error)} />;
  const prompts = q.data?.prompts ?? [];
  if (prompts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
        No prompts uploaded for {cell} / {language}. Click <strong>New prompt</strong> above.
      </div>
    );
  }
  // Show non-deleted versions on top; deleted ones (admin-visible only)
  // sink to the bottom but still appear so the operator can audit.
  const groups = groupByName(prompts).map((g) => ({
    ...g,
    isAllDeleted: g.versions.every((v) => v.deleted_at !== null),
  }));
  return (
    <div className="grid gap-2">
      {groups.map((g) => {
        const latest = g.versions[0];
        const olderVersions = g.versions.slice(1);
        const isGroupOpen = openGroups.has(g.name);
        const latestDeleted = latest.deleted_at !== null;
        return (
          <div
            key={g.name}
            className={cn(
              'rounded-lg border bg-card shadow-sm',
              selected?.name === g.name ? 'border-primary/30' : 'border-border',
              g.isAllDeleted && 'border-status-warning-border/50 bg-status-warning/30 opacity-80',
            )}
          >
            <button
              onClick={() => onSelect(latest)}
              className="flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-accent/30"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    <span className={cn(latestDeleted && 'line-through text-muted-foreground')}>
                      {g.name}
                    </span>{' '}
                    <span className="text-muted-foreground">latest v{latest.version}</span>
                    {latestDeleted && (
                      <span
                        title={
                          latest.deleted_by
                            ? `Deleted by ${latest.deleted_by}. Admin-visible only.`
                            : 'Deleted. Admin-visible only.'
                        }
                        className="ml-2 inline-flex items-center rounded-sm border border-status-warning-border/50 bg-status-warning px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-status-warning-text"
                      >
                        deleted{latest.deleted_by ? ` · ${latest.deleted_by}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                    {latest.source_filename}
                    {latest.owner_email && ` · ${latest.owner_email}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="tabular-nums">{latest.body_length.toLocaleString()} chars</span>
                <span className="tabular-nums">
                  {latest.operator_declared_count} / {latest.variable_count} vars
                </span>
                {g.versions.length > 1 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5">
                    <History className="size-3" /> {g.versions.length} versions
                  </span>
                )}
                <ChevronRight className="size-4" />
              </div>
            </button>
            {olderVersions.length > 0 && (
              <div className="border-t border-border bg-muted/20 px-4 py-2 text-xs">
                <button
                  onClick={() =>
                    setOpenGroups((s) => {
                      const next = new Set(s);
                      if (next.has(g.name)) next.delete(g.name);
                      else next.add(g.name);
                      return next;
                    })
                  }
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  {isGroupOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
                  {isGroupOpen ? 'hide history' : `show ${olderVersions.length} older`}
                </button>
                {isGroupOpen && (
                  <ul className="mt-2 space-y-1">
                    {olderVersions.map((v) => (
                      <li key={v.version}>
                        <button
                          onClick={() => onSelect(v)}
                          className={cn(
                            'flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left hover:bg-accent/40',
                            selected?.name === v.name && selected.version === v.version &&
                              'bg-accent/40',
                          )}
                        >
                          <span className={cn('font-mono', v.deleted_at && 'line-through text-muted-foreground')}>
                            v{v.version}{' '}
                            <span className="text-muted-foreground">— read-only</span>
                            {v.deleted_at && (
                              <span className="ml-2 not-italic text-status-warning-text normal-case">
                                deleted{v.deleted_by ? ` by ${v.deleted_by}` : ''}
                              </span>
                            )}
                          </span>
                          <span className="tabular-nums text-muted-foreground">
                            {v.body_length.toLocaleString()} chars
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Detail panel — body view + edit + variable form
// ────────────────────────────────────────────────────────────────────

function PromptDetailPanel({
  cell,
  language,
  name,
  version,
  onVersionChange,
}: {
  cell: string;
  language: Language;
  name: string;
  version: number;
  onVersionChange: (v: number) => void;
}) {
  const qc = useQueryClient();
  const promptQ = useQuery({
    queryKey: ['prompt', cell, language, name, version],
    queryFn: () => api.getPrompt(cell, language, name, version),
  });
  const personasQ = useQuery({
    queryKey: ['personas', cell, language],
    queryFn: () => api.listPersonas(cell, language),
    retry: 0,
  });
  const versionsQ = useQuery({
    queryKey: ['versions', cell, language, name],
    queryFn: () => api.listPromptVersions(cell, language, name),
    retry: 0,
  });

  const [editBody, setEditBody] = useState<string | null>(null);

  const bumpBodyMut = useMutation({
    mutationFn: () =>
      api.bumpPromptVersion(cell, language, name, {
        body: editBody ?? '',
        variable_definitions: promptQ.data?.variable_definitions ?? [],
      }),
    onSuccess: (saved) => {
      toast.success(`Saved as v${saved.version}`);
      setEditBody(null);
      qc.invalidateQueries({ queryKey: ['prompts', cell, language] });
      qc.invalidateQueries({ queryKey: ['versions', cell, language, name] });
      onVersionChange(saved.version);
    },
    onError: (e) => toast.error(String(e)),
  });

  if (promptQ.isLoading || personasQ.isLoading) return <PageLoading />;
  if (promptQ.error) return <PageError message={String(promptQ.error)} />;
  if (!promptQ.data) return null;

  const isLatest =
    versionsQ.data === undefined || version === Math.max(...(versionsQ.data || [version]));

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
      <PromptHeader
        prompt={promptQ.data}
        isLatest={isLatest}
        allVersions={versionsQ.data ?? []}
        onVersionChange={onVersionChange}
      />

      <PromptBodyView
        prompt={promptQ.data}
        isLatest={isLatest}
        editBody={editBody}
        setEditBody={setEditBody}
        onSave={() => bumpBodyMut.mutate()}
        isSaving={bumpBodyMut.isPending}
      />

      <DefinitionsPanel
        cell={cell}
        language={language}
        prompt={promptQ.data}
        personas={personasQ.data?.personas ?? []}
        isLatest={isLatest}
        onVersionChange={onVersionChange}
      />

      <hr className="border-border" />
      <VariableForm prompt={promptQ.data} personas={personasQ.data?.personas ?? []} />

      <hr className="border-border" />
      <TranscriptJudgePanel prompt={promptQ.data} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Judge a REAL pasted transcript against this prompt's cell rulebooks
// ────────────────────────────────────────────────────────────────────

const FACT_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'today', label: 'Call date (today)', placeholder: 'YYYY-MM-DD' },
  { key: 'due_date', label: 'Due date', placeholder: 'YYYY-MM-DD' },
  { key: 'latest_allowed_ptp_date', label: 'Latest allowed PTP date', placeholder: 'YYYY-MM-DD' },
  { key: 'overdue_amount', label: 'Overdue amount', placeholder: 'e.g. 2,760.00 AED' },
  { key: 'installment_amount', label: 'Installment amount', placeholder: 'e.g. 2,760.00 AED' },
  { key: 'total_outstanding', label: 'Total outstanding', placeholder: 'e.g. 29,140 AED' },
  { key: 'agent_name', label: 'Agent name (as said on the call)', placeholder: 'e.g. Sarah' },
];

function TranscriptJudgePanel({ prompt }: { prompt: PromptDetail }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [facts, setFacts] = useState<Record<string, string>>({});

  const judgeMut = useMutation({
    mutationFn: () =>
      api.judgeTranscript(prompt.cell_slug, prompt.language, prompt.name, prompt.version, {
        transcript_text: transcript,
        facts: Object.fromEntries(
          Object.entries(facts).filter(([, v]) => v.trim() !== ''),
        ),
      }),
    onSuccess: (r) => {
      toast.success(`Judged real call — ${r.finding_count} finding(s) across ${r.turn_count} turns`);
      navigate({ to: '/runs', search: { rid: r.run_id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const turnGuess = transcript
    .split('\n')
    .filter((l) => /^\s*(user|bot|customer|agent|caller|client|human|assistant|ai)\s*:?\s*$/i.test(l) ||
      /^\s*(user|bot|customer|agent|caller|client|human|assistant|ai)\s*:\s*\S/i.test(l)).length;

  return (
    <section className="rounded-md border border-border bg-muted/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium hover:bg-accent/30"
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        Judge a real call transcript
        <span className="text-xs font-normal text-muted-foreground">
          — paste a production call; grade it against this cell's rulebooks
        </span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-border p-4">
          <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground">
            Paste a real call below. Label each turn with <code className="font-mono">User</code> (the
            customer) and <code className="font-mono">Bot</code> (the agent), each on its own line
            before the text — exactly how your transcripts already look. The Judge grades the bot
            against this cell's <strong>rulebook, conversation design, and verbalism</strong> plus any
            approved "judge-wrong" calibrations and the shared checklist for{' '}
            <strong>{prompt.agent_type ?? 'this prompt (untyped)'}</strong>. The prompt's own text is
            not used. Result opens as an <strong>imported run</strong>.
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Transcript {turnGuess > 0 && <span className="tabular-nums">· ~{turnGuess} turns detected</span>}
            </span>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={12}
              placeholder={'User\n\nhi\n\nBot\n\nHello, good afternoon. This call is recorded...'}
              className="w-full rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>

          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">
              Call facts (optional) — fill what you know so the Judge can fact-check quoted dates &
              amounts. Anything left blank is simply not checked.
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {FACT_FIELDS.map((f) => (
                <label key={f.key} className="flex flex-col gap-1">
                  <span className="text-[11px] text-muted-foreground">{f.label}</span>
                  <input
                    value={facts[f.key] ?? ''}
                    onChange={(e) => setFacts((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="h-8 rounded-md border border-input bg-card px-2.5 text-xs shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              Judged against all 3 registers for {prompt.cell_slug} / {prompt.language}.
            </span>
            <button
              onClick={() => judgeMut.mutate()}
              disabled={judgeMut.isPending || transcript.trim().length === 0}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {judgeMut.isPending ? <Spinner className="size-4 text-primary-foreground" /> : <PlayCircle className="size-4" />}
              {judgeMut.isPending ? 'Judging…' : 'Judge this call'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Definitions panel — declare what unknown variables mean
// ────────────────────────────────────────────────────────────────────

function DefinitionsPanel({
  cell,
  language,
  prompt,
  personas,
  isLatest,
  onVersionChange,
}: {
  cell: string;
  language: Language;
  prompt: PromptDetail;
  personas: PersonaListItem[];
  isLatest: boolean;
  onVersionChange: (v: number) => void;
}) {
  const qc = useQueryClient();
  const unknownNames = useMemo(
    () => extractUnknownVariables(prompt.body),
    [prompt.body],
  );

  // The existing definitions on this prompt + an empty slot for any
  // unknown that doesn't have one yet.
  const initialDefs: VariableDefinition[] = useMemo(() => {
    const byName = new Map(prompt.variable_definitions.map((d) => [d.name, d]));
    return unknownNames.map((name) => byName.get(name) ?? emptyDefinition(name));
  }, [prompt.variable_definitions, unknownNames]);

  const [defs, setDefs] = useState<VariableDefinition[]>(initialDefs);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDefs(initialDefs);
  }, [initialDefs]);

  const allComplete = defs.every((d) => checkComplete(d, personas));
  const dirty = JSON.stringify(defs) !== JSON.stringify(initialDefs);

  const saveMut = useMutation({
    mutationFn: () =>
      api.bumpPromptVersion(cell, language, prompt.name, {
        body: prompt.body,
        variable_definitions: defs,
      }),
    onSuccess: (saved) => {
      toast.success(`Definitions saved as v${saved.version}`);
      qc.invalidateQueries({ queryKey: ['prompts', cell, language] });
      qc.invalidateQueries({ queryKey: ['versions', cell, language, prompt.name] });
      qc.invalidateQueries({ queryKey: ['prompt', cell, language, prompt.name, saved.version] });
      onVersionChange(saved.version);
      setOpen(false);
    },
    onError: (e) => toast.error(String(e)),
  });

  const autoFillMut = useMutation({
    mutationFn: () => {
      const undefinedNames = defs
        .filter((d) => !checkComplete(d, personas))
        .map((d) => d.name);
      return api.autoDefinePrompt(cell, language, {
        body: prompt.body,
        unknown_variable_names: undefinedNames,
      });
    },
    onSuccess: (resp) => {
      // Merge: keep existing operator-filled values, only fill empties
      // with Sonnet's suggestions.
      const suggested = new Map(resp.definitions.map((d) => [d.name, d]));
      setDefs((prev) =>
        prev.map((d) => {
          const sug = suggested.get(d.name);
          if (!sug) return d;
          if (d.kind === 'fixed' && d.fixed_value && d.fixed_value.trim()) {
            return d;
          }
          // Merge per-persona values: prefer existing non-empty over suggestion
          const merged: Record<string, string> = { ...sug.per_persona_values };
          for (const [pid, v] of Object.entries(d.per_persona_values)) {
            if (v && v.trim()) merged[pid] = v;
          }
          return {
            ...d,
            kind: 'per_persona',
            per_persona_values: merged,
            description: d.description || sug.description,
          };
        }),
      );
      toast.success(
        `Auto-filled ${resp.definitions.length} variable${resp.definitions.length === 1 ? '' : 's'} — review before saving`,
      );
      setOpen(true);
    },
    onError: (e) => toast.error(String(e)),
  });

  if (unknownNames.length === 0) {
    // No unknowns → no definitions needed.
    return null;
  }

  const undefinedCount = defs.filter((d) => !checkComplete(d, personas)).length;

  return (
    <section
      className={cn(
        'rounded-md border bg-card',
        undefinedCount > 0 && isLatest ? 'border-status-error-border' : 'border-border',
      )}
    >
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left',
          open && 'border-b border-border',
        )}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          <span className="text-sm font-medium">Variable definitions</span>
          <span className="text-xs text-muted-foreground">
            ({unknownNames.length} unknown variable{unknownNames.length === 1 ? '' : 's'} in this prompt)
          </span>
        </div>
        {undefinedCount > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-status-error-border bg-status-error px-2 py-0.5 text-xs font-medium text-status-error-text">
            <AlertCircle className="size-3.5" />
            {undefinedCount} need definition
          </span>
        ) : (
          <span className="rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
            all defined
          </span>
        )}
      </button>

      {open && (
        <div className="space-y-3 p-4">
          {!isLatest && (
            <div className="rounded-md border border-status-warning-border bg-status-warning p-3 text-xs text-status-warning-text">
              You're viewing an older version. Definition edits will create a new version off the LATEST, not off this one.
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground">
              Don't want to type every value? Sonnet can suggest realistic per-persona values based on the prompt body + persona scenarios.
            </div>
            <button
              onClick={() => autoFillMut.mutate()}
              disabled={autoFillMut.isPending || undefinedCount === 0}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/40 bg-card px-3 text-xs font-medium text-primary shadow-sm hover:bg-accent disabled:opacity-50"
            >
              {autoFillMut.isPending ? (
                <Spinner className="size-3" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {autoFillMut.isPending ? 'Generating…' : 'Auto-fill with AI'}
            </button>
          </div>

          {defs.map((d, i) => (
            <DefinitionEditor
              key={d.name}
              variable={d.name}
              personas={personas}
              loadingPersonas={false}
              value={d}
              onChange={(next) => {
                const copy = [...defs];
                copy[i] = next;
                setDefs(copy);
              }}
            />
          ))}
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <span className="text-xs text-muted-foreground">
              {dirty ? 'unsaved changes' : 'no changes'}
            </span>
            <button
              onClick={() => setDefs(initialDefs)}
              disabled={!dirty}
              className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
            >
              Reset
            </button>
            <button
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || !dirty || !allComplete}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {saveMut.isPending ? (
                <Spinner className="size-3 text-primary-foreground" />
              ) : (
                <Save className="size-3.5" />
              )}
              Save (bumps to v{prompt.version + 1})
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function PromptHeader({
  prompt,
  isLatest,
  allVersions,
  onVersionChange,
}: {
  prompt: PromptDetail;
  isLatest: boolean;
  allVersions: number[];
  onVersionChange: (v: number) => void;
}) {
  const operator = prompt.variables.filter((v) => v.kind === 'operator_declared');
  const auto = prompt.variables.filter((v) => v.kind === 'auto_handled');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [diffOpen, setDiffOpen] = useState(false);

  const promptDeleted = prompt.deleted_at !== null;
  const deleteVersionMut = useMutation({
    mutationFn: () =>
      api.deletePromptVersion(prompt.cell_slug, prompt.language, prompt.name, prompt.version),
    onSuccess: () => {
      toast.success(
        promptDeleted
          ? `Purged v${prompt.version} from disk`
          : `Soft-deleted v${prompt.version}`,
      );
      qc.invalidateQueries({ queryKey: ['prompts', prompt.cell_slug, prompt.language] });
      qc.invalidateQueries({ queryKey: ['versions', prompt.cell_slug, prompt.language, prompt.name] });
      const remaining = allVersions.filter((v) => v !== prompt.version);
      if (remaining.length > 0) onVersionChange(Math.max(...remaining));
      else navigate({ to: '/prompts' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletePromptMut = useMutation({
    mutationFn: () => api.deletePrompt(prompt.cell_slug, prompt.language, prompt.name),
    onSuccess: () => {
      toast.success(
        promptDeleted
          ? `Purged ${prompt.name} (all ${allVersions.length} version${allVersions.length === 1 ? '' : 's'}) from disk`
          : `Soft-deleted ${prompt.name} (all ${allVersions.length} version${allVersions.length === 1 ? '' : 's'})`,
      );
      qc.invalidateQueries({ queryKey: ['prompts', prompt.cell_slug, prompt.language] });
      navigate({ to: '/prompts' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const promoteMut = useMutation({
    mutationFn: () =>
      api.promotePromptVersion(prompt.cell_slug, prompt.language, prompt.name, prompt.version),
    onSuccess: (saved) => {
      toast.success(`Promoted v${prompt.version} → new v${saved.version}`);
      qc.invalidateQueries({ queryKey: ['prompts', prompt.cell_slug, prompt.language] });
      qc.invalidateQueries({ queryKey: ['versions', prompt.cell_slug, prompt.language, prompt.name] });
      onVersionChange(saved.version);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function confirmDeleteVersion() {
    if (
      window.confirm(
        promptDeleted
          ? `PERMANENTLY PURGE ${prompt.name} v${prompt.version} from disk?\n\nThis version is already soft-deleted. Confirming will unlink the .md / _meta.json / vars.json files. Cannot be undone.`
          : `Delete ${prompt.name} v${prompt.version}?\n\nThis soft-deletes the version — admins still see it marked "deleted by you". You can purge it from disk later.`,
      )
    ) deleteVersionMut.mutate();
  }
  function confirmDeletePrompt() {
    if (
      window.confirm(
        promptDeleted
          ? `PERMANENTLY PURGE ${prompt.name} (all ${allVersions.length} version${allVersions.length === 1 ? '' : 's'}) from disk?\n\nAll versions are already soft-deleted. Confirming will unlink every file. Cannot be undone.`
          : `Delete ${prompt.name} ENTIRELY (all ${allVersions.length} version${allVersions.length === 1 ? '' : 's'})?\n\nThis soft-deletes all versions — admins still see them. You can purge from disk later.`,
      )
    ) deletePromptMut.mutate();
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="truncate">
          <span className={cn(promptDeleted && 'line-through text-muted-foreground')}>
            {prompt.name}
          </span>{' '}
          <span className="text-muted-foreground">v{prompt.version}</span>
          {!isLatest && (
            <span className="ml-2 rounded-full border border-status-warning-border bg-status-warning px-2 py-0.5 text-xs font-medium text-status-warning-text">
              older version — read-only
            </span>
          )}
          {promptDeleted && (
            <span
              title={
                prompt.deleted_by
                  ? `Soft-deleted by ${prompt.deleted_by}. Visible only to admins.`
                  : 'Soft-deleted. Visible only to admins.'
              }
              className="ml-2 inline-flex items-center rounded-full border border-status-warning-border/50 bg-status-warning px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-status-warning-text"
            >
              deleted{prompt.deleted_by ? ` · ${prompt.deleted_by}` : ''}
            </span>
          )}
        </h2>
        <div className="text-xs text-muted-foreground">
          {prompt.body_length.toLocaleString()} chars · {prompt.line_count} lines
        </div>
      </div>
      <div className="font-mono text-xs text-muted-foreground">
        {prompt.source_filename}
        {prompt.owner_email && ` · owned by ${prompt.owner_email}`}
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Pill>{operator.length} operator-declared</Pill>
        <Pill>{auto.length} auto-handled</Pill>
        <AgentTypePicker prompt={prompt} />
        <div className="ml-auto flex items-center gap-1.5">
          {allVersions.length > 1 && (
            <button
              onClick={() => setDiffOpen(true)}
              title="Diff between any two versions"
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <GitCompare className="size-3.5" />
              Diff
            </button>
          )}
          {!isLatest && (
            <button
              onClick={() => promoteMut.mutate()}
              disabled={promoteMut.isPending}
              title={`Promote v${prompt.version} → new v${Math.max(...allVersions) + 1}`}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-primary hover:bg-accent disabled:opacity-50"
            >
              {promoteMut.isPending ? <Spinner className="size-3" /> : <ArrowUpFromLine className="size-3.5" />}
              Make this the latest
            </button>
          )}
          {allVersions.length > 1 && (
            <button
              onClick={confirmDeleteVersion}
              disabled={deleteVersionMut.isPending}
              title={
                promptDeleted
                  ? `Purge v${prompt.version} from disk (admin only — already soft-deleted)`
                  : `Soft-delete v${prompt.version}`
              }
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-medium disabled:opacity-50',
                promptDeleted
                  ? 'border-destructive/40 bg-destructive/10 text-destructive hover:border-destructive hover:bg-destructive hover:text-destructive-foreground'
                  : 'border-border text-muted-foreground hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
              )}
            >
              {deleteVersionMut.isPending ? <Spinner className="size-3" /> : <Trash2 className="size-3.5" />}
              {promptDeleted ? `Purge v${prompt.version}` : `Delete v${prompt.version}`}
            </button>
          )}
          <button
            onClick={confirmDeletePrompt}
            disabled={deletePromptMut.isPending}
            title={
              promptDeleted
                ? 'Purge this prompt entirely from disk (admin only — already soft-deleted)'
                : 'Soft-delete this prompt entirely (all versions; admins keep visibility)'
            }
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium disabled:opacity-50',
              promptDeleted
                ? 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10',
            )}
          >
            {deletePromptMut.isPending ? <Spinner className="size-3" /> : <Trash2 className="size-3.5" />}
            {promptDeleted ? 'Purge prompt' : 'Delete prompt'}
          </button>
        </div>
      </div>
      {diffOpen && (
        <DiffModal
          cell={prompt.cell_slug}
          language={prompt.language}
          name={prompt.name}
          versions={allVersions}
          initialFrom={
            allVersions.length >= 2 ? allVersions[allVersions.length - 2] : allVersions[0]
          }
          initialTo={allVersions[allVersions.length - 1]}
          onClose={() => setDiffOpen(false)}
        />
      )}
    </div>
  );
}

function AgentTypePicker({ prompt }: { prompt: PromptDetail }) {
  const qc = useQueryClient();
  const typesQ = useQuery({ queryKey: ['agent-types'], queryFn: api.listAgentTypes });
  const setMut = useMutation({
    mutationFn: (slug: string) =>
      api.setPromptAgentType(
        prompt.cell_slug, prompt.language, prompt.name, slug || null,
      ),
    onSuccess: (resp) => {
      toast.success(
        resp.agent_type
          ? `Tagged ${prompt.name} as ${resp.agent_type} (${resp.versions_updated} version${resp.versions_updated === 1 ? '' : 's'})`
          : `Cleared agent type on ${prompt.name}`,
      );
      qc.invalidateQueries({ queryKey: ['prompts', prompt.cell_slug, prompt.language] });
      qc.invalidateQueries({ queryKey: ['prompt', prompt.cell_slug, prompt.language, prompt.name] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <label
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
      title="Which agent this prompt implements — pre-selects matching personas at run start and tells the judge the call type. Applies to all versions."
    >
      <span className="text-muted-foreground">Agent type</span>
      <select
        value={prompt.agent_type ?? ''}
        onChange={(e) => setMut.mutate(e.target.value)}
        disabled={setMut.isPending}
        className="bg-transparent text-xs font-medium focus:outline-none disabled:opacity-50"
      >
        <option value="">— not set —</option>
        {(typesQ.data ?? []).map((t) => (
          <option key={t.slug} value={t.slug}>
            {t.label}
          </option>
        ))}
      </select>
      {setMut.isPending && <Spinner className="size-3" />}
    </label>
  );
}

function DiffModal({
  cell,
  language,
  name,
  versions,
  initialFrom,
  initialTo,
  onClose,
}: {
  cell: string;
  language: Language;
  name: string;
  versions: number[];
  initialFrom: number;
  initialTo: number;
  onClose: () => void;
}) {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const diffQ = useQuery({
    queryKey: ['diff', cell, language, name, from, to],
    queryFn: () => api.diffPromptVersions(cell, language, name, from, to),
    enabled: from !== to,
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay-scrim p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            Diff — {name}
          </h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent">
            <X className="size-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">from</span>
            <select
              value={from}
              onChange={(e) => setFrom(parseInt(e.target.value, 10))}
              className={inputCls}
            >
              {versions.map((v) => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
          </label>
          <span className="text-muted-foreground">→</span>
          <label className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">to</span>
            <select
              value={to}
              onChange={(e) => setTo(parseInt(e.target.value, 10))}
              className={inputCls}
            >
              {versions.map((v) => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-4 flex-1 overflow-auto rounded-md border border-border bg-background p-3 text-xs">
          {from === to && (
            <div className="text-muted-foreground">Pick two different versions to diff.</div>
          )}
          {diffQ.isLoading && <Spinner className="size-4" />}
          {diffQ.data && (
            <pre className="whitespace-pre-wrap font-mono leading-relaxed">
              {diffQ.data.unified_diff
                ? diffQ.data.unified_diff.split('\n').map((line, i) => {
                    const cls =
                      line.startsWith('+') && !line.startsWith('+++')
                        ? 'text-success'
                        : line.startsWith('-') && !line.startsWith('---')
                        ? 'text-destructive'
                        : line.startsWith('@@')
                        ? 'text-status-warning-text'
                        : '';
                    return (
                      <div key={i} className={cls}>
                        {line || ' '}
                      </div>
                    );
                  })
                : 'No differences between these versions.'}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function PromptBodyView({
  prompt,
  isLatest,
  editBody,
  setEditBody,
  onSave,
  isSaving,
}: {
  prompt: PromptDetail;
  isLatest: boolean;
  editBody: string | null;
  setEditBody: (b: string | null) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const [open, setOpen] = useState(true);
  const editing = editBody !== null;
  return (
    <section className="rounded-md border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-sm font-medium hover:text-foreground"
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          Prompt body
        </button>
        {open && isLatest && !editing && (
          <button
            onClick={() => setEditBody(prompt.body)}
            className="inline-flex h-7 items-center rounded-md border border-border bg-card px-2.5 text-xs font-medium hover:bg-accent"
          >
            Edit (bumps to v{prompt.version + 1})
          </button>
        )}
      </div>
      {open && (
        <div className="p-4">
          {editing ? (
            <>
              <textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={20}
                className="w-full rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <span className="text-xs text-muted-foreground">
                  Saving creates v{prompt.version + 1}; v{prompt.version} stays read-only.
                </span>
                <button
                  onClick={() => setEditBody(null)}
                  className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={isSaving || !editBody.trim() || editBody === prompt.body}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
                >
                  {isSaving ? (
                    <Spinner className="size-3 text-primary-foreground" />
                  ) : (
                    <Save className="size-3.5" />
                  )}
                  Save as v{prompt.version + 1}
                </button>
              </div>
            </>
          ) : (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-xs leading-relaxed">
              {prompt.body}
            </pre>
          )}
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Variable form (unchanged from previous commit)
// ────────────────────────────────────────────────────────────────────

function VariableForm({
  prompt,
  personas,
}: {
  prompt: PromptDetail;
  personas: PersonaListItem[];
}) {
  const navigate = useNavigate();
  const [today, setToday] = useState(() => new Date().toISOString().slice(0, 10));
  // When the prompt is tagged with an agent type, default-select ONLY
  // the personas whose call_type matches — a Reminder agent shouldn't
  // be graded against Broken-PTP customers (operator 2026-06-10).
  // Unknown/typo call_type tags count as matching so a bad tag never
  // hides a persona. Untyped prompts keep the select-all default.
  const matchesAgentType = (p: PersonaListItem) => {
    if (!prompt.agent_type) return true;
    const t = normalizeCallType(p.call_type);
    return t === null || t === prompt.agent_type;
  };
  const defaultSelection = () =>
    new Set(personas.filter(matchesAgentType).map((p) => p.persona_id));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(defaultSelection);

  // "Preview persona" — which persona's resolved values get prefilled
  // into the variable inputs below. Defaults to the first selected.
  const previewPersonaId = useMemo(
    () => {
      const first = personas.find((p) => selectedIds.has(p.persona_id));
      return first?.persona_id ?? personas[0]?.persona_id ?? '';
    },
    [personas, selectedIds],
  );

  /** Per-variable input values. Pre-filled from /api/state/derive's
   *  ``resolved`` map, then operator-editable. Applied as overrides to
   *  the preview persona only — the other selected personas use their
   *  own derived state. */
  const [inputs, setInputs] = useState<Record<string, string>>({});

  const operatorDeclared = useMemo(
    () => prompt.variables.filter((v) => v.kind === 'operator_declared'),
    [prompt.variables],
  );

  // Re-seed selection when the persona list or the prompt's agent type
  // changes (cell switch, re-tag etc.)
  useEffect(() => {
    setSelectedIds(defaultSelection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas, prompt.agent_type]);

  const offTypeCount = prompt.agent_type
    ? personas.filter((p) => !matchesAgentType(p)).length
    : 0;

  // Auto-derive on preview persona / today / prompt change. The resolved
  // map pre-fills the variable inputs for the preview persona so the
  // operator sees realistic values + can edit any one as a per-run
  // override.
  const deriveQ = useQuery({
    queryKey: [
      'derive',
      prompt.cell_slug,
      prompt.language,
      prompt.name,
      prompt.version,
      previewPersonaId,
      today,
    ],
    queryFn: () =>
      api.deriveState({
        cell_slug: prompt.cell_slug,
        language: prompt.language,
        prompt_name: prompt.name,
        prompt_version: prompt.version,
        persona_ids: [previewPersonaId],
        today,
      }),
    enabled: !!previewPersonaId,
    staleTime: 60_000,
  });

  useEffect(() => {
    const resolved = deriveQ.data?.resolved?.[previewPersonaId];
    if (resolved) {
      setInputs(resolved);
    }
  }, [deriveQ.data, previewPersonaId]);

  const selectedCount = selectedIds.size;

  // Per-run LLM + temperature picker. Persisted to localStorage so the
  // operator's choices stick across reloads — but stored as "use
  // default" (null) until they actually open the gear and pick.
  type ModelChoice = { agent: string | null; persona: string | null; judge: string | null };
  type TempChoice = { agent: number | null; persona: number | null; judge: number | null };
  const [models, setModels] = useState<ModelChoice>(() => {
    try {
      const raw = localStorage.getItem('scribe-v9.run-models');
      if (raw) return JSON.parse(raw);
    } catch {
      /* ignore */
    }
    return { agent: null, persona: null, judge: null };
  });
  const [temperatures, setTemperatures] = useState<TempChoice>(() => {
    try {
      const raw = localStorage.getItem('scribe-v9.run-temperatures');
      if (raw) return JSON.parse(raw);
    } catch {
      /* ignore */
    }
    return { agent: null, persona: null, judge: null };
  });
  useEffect(() => {
    try {
      localStorage.setItem('scribe-v9.run-models', JSON.stringify(models));
    } catch {
      /* ignore */
    }
  }, [models]);
  useEffect(() => {
    try {
      localStorage.setItem('scribe-v9.run-temperatures', JSON.stringify(temperatures));
    } catch {
      /* ignore */
    }
  }, [temperatures]);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const overrideCount =
    (models.agent ? 1 : 0) +
    (models.persona ? 1 : 0) +
    (models.judge ? 1 : 0) +
    (temperatures.agent !== null ? 1 : 0) +
    (temperatures.persona !== null ? 1 : 0) +
    (temperatures.judge !== null ? 1 : 0);

  const startRunMut = useMutation({
    mutationFn: () => {
      const personaIds = Array.from(selectedIds);
      const overridesForPreview = Object.fromEntries(
        Object.entries(inputs).filter(([, v]) => v.trim() !== ''),
      );
      const overrides: Record<string, Record<string, string>> = {};
      // Only the preview persona gets the input overrides. Other
      // selected personas use their own derived state.
      if (previewPersonaId && Object.keys(overridesForPreview).length > 0) {
        overrides[previewPersonaId] = overridesForPreview;
      }
      return api.startRun({
        cell_slug: prompt.cell_slug,
        language: prompt.language,
        prompt_name: prompt.name,
        prompt_version: prompt.version,
        persona_ids: personaIds,
        today,
        overrides,
        agent_model: models.agent,
        persona_model: models.persona,
        judge_model: models.judge,
        agent_temperature: temperatures.agent,
        persona_temperature: temperatures.persona,
        judge_temperature: temperatures.judge,
      });
    },
    onSuccess: (run) => {
      toast.success(
        `Run ${run.run_id} started for ${selectedCount} persona${selectedCount === 1 ? '' : 's'}`,
      );
      navigate({ to: '/runs', search: { rid: run.run_id } });
    },
    onError: (e) => toast.error(String(e)),
  });

  if (personas.length === 0) {
    return (
      <PageError message="No personas in this cell yet — upload personas before exercising the variable form." />
    );
  }
  if (!previewPersonaId) return null;

  const state = deriveQ.data?.states[previewPersonaId];
  const resolvedForPersona = deriveQ.data?.resolved?.[previewPersonaId] ?? {};
  const emptyVarCount = operatorDeclared.filter(
    (v) => (inputs[v.name] ?? '').trim() === '',
  ).length;

  return (
    <div className="space-y-6">
      {offTypeCount > 0 && (
        <div className="rounded-md border border-sky-300/60 bg-sky-50/60 px-3 py-2 text-xs text-sky-900">
          This prompt is tagged <strong>{prompt.agent_type}</strong> — {offTypeCount} persona
          {offTypeCount === 1 ? '' : 's'} of other call types {offTypeCount === 1 ? 'is' : 'are'} left
          unselected below. Tick them manually if you want a mismatch test (the judge will be told).
        </div>
      )}
      <PersonaMultiSelect
        personas={personas}
        selected={selectedIds}
        onChange={setSelectedIds}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <FieldLabel label="Preview persona (for prefill below)">
          <select
            value={previewPersonaId}
            onChange={(e) => {
              // Make sure the preview persona is also selected for the run.
              setSelectedIds((s) => new Set([...s, e.target.value]));
            }}
            className={inputCls}
          >
            {personas
              .filter((p) => selectedIds.has(p.persona_id))
              .map((p) => (
                <option key={p.persona_id} value={p.persona_id}>
                  {p.name} — {p.persona_id}
                </option>
              ))}
          </select>
        </FieldLabel>
        <FieldLabel label="Today (Asia/Dubai)">
          <input
            type="date"
            value={today}
            onChange={(e) => setToday(e.target.value)}
            className={inputCls}
          />
        </FieldLabel>
        <div className="flex items-end gap-2">
          {deriveQ.isFetching && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Spinner className="size-3.5" /> computing…
            </span>
          )}
          {!deriveQ.isFetching && deriveQ.isSuccess && (
            <span className="text-xs text-muted-foreground">
              prefilled from rule engine + glossary
            </span>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Variables in this prompt ({operatorDeclared.length} operator-declared)
          </div>
          {emptyVarCount > 0 && (
            <span className="text-xs text-status-error-text">
              {emptyVarCount} need value
            </span>
          )}
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {operatorDeclared.map((v) => {
            const value = inputs[v.name] ?? '';
            const resolved = resolvedForPersona[v.name] ?? '';
            const isUnresolved = resolved.trim() === '';
            const edited = value !== resolved;
            const empty = value.trim() === '';
            return (
              <div
                key={v.name}
                className={cn(
                  'rounded-md border bg-card p-3 text-sm shadow-sm',
                  empty && isUnresolved
                    ? 'border-status-error-border bg-status-error/40'
                    : 'border-border',
                )}
              >
                <div className="flex items-center justify-between">
                  <code className="font-mono text-xs">{v.raw}</code>
                  <span className="text-xs text-muted-foreground">×{v.occurrences}</span>
                </div>
                {v.example_from_dict && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    ex (from vardict): {v.example_from_dict}
                  </div>
                )}
                <input
                  value={value}
                  onChange={(e) =>
                    setInputs((o) => ({ ...o, [v.name]: e.target.value }))
                  }
                  placeholder={
                    isUnresolved
                      ? 'no rule-engine value — type one'
                      : 'edit to override'
                  }
                  className={cn(
                    inputCls,
                    'mt-2 w-full',
                    empty && isUnresolved &&
                      'border-status-error-border focus-visible:ring-status-error-border',
                  )}
                />
                <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                  {edited && !empty && (
                    <button
                      onClick={() =>
                        setInputs((o) => ({ ...o, [v.name]: resolved }))
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      reset to derived ({resolved || 'empty'})
                    </button>
                  )}
                  {!edited && resolved && !empty && (
                    <span className="text-muted-foreground">
                      derived
                    </span>
                  )}
                  {edited && !empty && (
                    <span className="ml-auto text-status-warning-text">edited</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {state && <PersonaStatePreview state={state} />}

      <PreflightStatus errors={deriveQ.data?.preflight_errors ?? []} />

      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <span className="text-xs text-muted-foreground">
          {selectedCount} persona{selectedCount === 1 ? '' : 's'} selected · ~{Math.ceil((selectedCount * 60) / 5)}s end-to-end with concurrency 5
        </span>
        <button
          onClick={() => setModelPickerOpen(true)}
          title="Choose LLM for agent / personas / judge for this run"
          className={cn(
            'relative inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium text-muted-foreground shadow-sm hover:bg-accent',
            overrideCount > 0 && 'border-primary/40 text-primary',
          )}
        >
          <Settings className="size-4" />
          Models
          {overrideCount > 0 && (
            <span className="ml-1 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {overrideCount}
            </span>
          )}
        </button>
        <button
          onClick={() => startRunMut.mutate()}
          disabled={
            startRunMut.isPending ||
            !state ||
            selectedCount === 0 ||
            (deriveQ.data?.preflight_errors ?? []).length > 0
          }
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors',
            'hover:bg-primary/90 disabled:opacity-60',
          )}
        >
          {startRunMut.isPending ? (
            <Spinner className="size-4 text-primary-foreground" />
          ) : (
            <PlayCircle className="size-4" />
          )}
          {startRunMut.isPending
            ? 'Starting run…'
            : `Start run for ${selectedCount} persona${selectedCount === 1 ? '' : 's'}`}
        </button>
      </div>

      {modelPickerOpen && (
        <ModelPickerModal
          value={models}
          temperatures={temperatures}
          onChange={setModels}
          onTemperaturesChange={setTemperatures}
          onClose={() => setModelPickerOpen(false)}
        />
      )}

      {deriveQ.error && <PageError message={String(deriveQ.error)} />}
      {startRunMut.error && <PageError message={String(startRunMut.error)} />}
    </div>
  );
}

function ModelPickerModal({
  value,
  temperatures,
  onChange,
  onTemperaturesChange,
  onClose,
}: {
  value: { agent: string | null; persona: string | null; judge: string | null };
  temperatures: { agent: number | null; persona: number | null; judge: number | null };
  onChange: (v: { agent: string | null; persona: string | null; judge: string | null }) => void;
  onTemperaturesChange: (v: { agent: number | null; persona: number | null; judge: number | null }) => void;
  onClose: () => void;
}) {
  const catQ = useQuery({ queryKey: ['models'], queryFn: api.listModels });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay-scrim p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Models & temperature for this run</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Defaults are shown and editable. Picks persist across reloads. Each role takes the temperature shown if you don't change it.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent">
            <X className="size-4" />
          </button>
        </div>
        {catQ.isLoading && (
          <div className="mt-4 flex justify-center"><Spinner className="size-5" /></div>
        )}
        {catQ.data && (
          <div className="mt-4 space-y-4">
            <ModelPickerRow
              label="Agent under test"
              hint="The LLM that plays the bank's collections agent (the system being graded)."
              entries={catQ.data.agent}
              selected={value.agent}
              defaultModelId={catQ.data.default_model.agent}
              defaultTemperature={catQ.data.default_temperature.agent}
              temperature={temperatures.agent}
              onSelect={(id) => onChange({ ...value, agent: id })}
              onTemperature={(t) => onTemperaturesChange({ ...temperatures, agent: t })}
            />
            <ModelPickerRow
              label="Persona simulator"
              hint="The LLM that plays the customer character. GPT-5 chat gives the most varied UAE-Arab personas."
              entries={catQ.data.persona}
              selected={value.persona}
              defaultModelId={catQ.data.default_model.persona}
              defaultTemperature={catQ.data.default_temperature.persona}
              temperature={temperatures.persona}
              onSelect={(id) => onChange({ ...value, persona: id })}
              onTemperature={(t) => onTemperaturesChange({ ...temperatures, persona: t })}
            />
            <ModelPickerRow
              label="Judge"
              hint="Grades watch_for criteria against the transcript. Sonnet 4.5 at temperature 0 is the strict default."
              entries={catQ.data.judge}
              selected={value.judge}
              defaultModelId={catQ.data.default_model.judge}
              defaultTemperature={catQ.data.default_temperature.judge}
              temperature={temperatures.judge}
              onSelect={(id) => onChange({ ...value, judge: id })}
              onTemperature={(t) => onTemperaturesChange({ ...temperatures, judge: t })}
            />
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={() => {
              onChange({ agent: null, persona: null, judge: null });
              onTemperaturesChange({ agent: null, persona: null, judge: null });
            }}
            className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium text-muted-foreground hover:bg-accent"
          >
            Reset to defaults
          </button>
          <button
            onClick={onClose}
            className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function ModelPickerRow({
  label,
  hint,
  entries,
  selected,
  defaultModelId,
  defaultTemperature,
  temperature,
  onSelect,
  onTemperature,
}: {
  label: string;
  hint: string;
  entries: { id: string; label: string }[];
  selected: string | null;
  defaultModelId: string;
  defaultTemperature: number;
  temperature: number | null;
  onSelect: (id: string | null) => void;
  onTemperature: (t: number | null) => void;
}) {
  // Show the resolved temperature value in the input (default if not
  // overridden, override if set). Display-only; the actual override
  // flag (null = use server default) lives in `temperature`.
  const effectiveTemperature = temperature ?? defaultTemperature;
  const tempEdited = temperature !== null;
  const modelEdited = selected !== null;
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>

      <label className="mt-2 block">
        <div className="mb-1 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          <span>Model</span>
          {!modelEdited && (
            <span className="text-muted-foreground/70 normal-case">
              default · {defaultModelId}
            </span>
          )}
          {modelEdited && <span className="text-primary normal-case">override</span>}
        </div>
        <select
          value={selected ?? '__default__'}
          onChange={(e) => onSelect(e.target.value === '__default__' ? null : e.target.value)}
          className={inputCls + ' w-full'}
        >
          <option value="__default__">Default ({defaultModelId})</option>
          {entries.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-2 block">
        <div className="mb-1 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          <span>Temperature</span>
          {!tempEdited && (
            <span className="text-muted-foreground/70 normal-case">
              default · {defaultTemperature}
            </span>
          )}
          {tempEdited && (
            <button
              type="button"
              onClick={() => onTemperature(null)}
              className="text-primary normal-case hover:underline"
            >
              reset to {defaultTemperature}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={2}
            step={0.1}
            value={effectiveTemperature}
            onChange={(e) => onTemperature(parseFloat(e.target.value))}
            className="flex-1"
          />
          <input
            type="number"
            min={0}
            max={2}
            step={0.1}
            value={effectiveTemperature}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v)) onTemperature(v);
            }}
            className={inputCls + ' w-20'}
          />
        </div>
      </label>
    </div>
  );
}

function PersonaMultiSelect({
  personas,
  selected,
  onChange,
}: {
  personas: PersonaListItem[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [collapsed, setCollapsed] = useState(true);

  // Group by expected_path so operator can toggle "all happy" / "all hostile" etc.
  const byExpected = useMemo(() => {
    const m = new Map<string, PersonaListItem[]>();
    for (const p of personas) {
      if (!m.has(p.expected_path)) m.set(p.expected_path, []);
      m.get(p.expected_path)!.push(p);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [personas]);

  const allSelected = selected.size === personas.length;
  const noneSelected = selected.size === 0;

  function toggleOne(pid: string) {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid);
    else next.add(pid);
    onChange(next);
  }

  function toggleGroup(group: PersonaListItem[]) {
    const allInGroup = group.every((p) => selected.has(p.persona_id));
    const next = new Set(selected);
    for (const p of group) {
      if (allInGroup) next.delete(p.persona_id);
      else next.add(p.persona_id);
    }
    onChange(next);
  }

  return (
    <section className="rounded-md border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 text-sm font-medium hover:text-foreground"
        >
          {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
          Personas to run
          <span className="text-xs text-muted-foreground">
            ({selected.size} / {personas.length} selected)
          </span>
        </button>
        <div className="flex items-center gap-1.5 text-xs">
          <button
            onClick={() => onChange(new Set(personas.map((p) => p.persona_id)))}
            disabled={allSelected}
            className="inline-flex items-center rounded border border-border bg-card px-2 py-1 font-medium hover:bg-accent disabled:opacity-50"
          >
            Select all
          </button>
          <button
            onClick={() => onChange(new Set())}
            disabled={noneSelected}
            className="inline-flex items-center rounded border border-border bg-card px-2 py-1 font-medium hover:bg-accent disabled:opacity-50"
          >
            Select none
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="space-y-3 p-4">
          {byExpected.map(([expected, group]) => {
            const allInGroup = group.every((p) => selected.has(p.persona_id));
            const someInGroup = group.some((p) => selected.has(p.persona_id));
            return (
              <div key={expected} className="rounded-md border border-border bg-muted/20 p-3">
                <button
                  onClick={() => toggleGroup(group)}
                  className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide"
                >
                  <input
                    type="checkbox"
                    checked={allInGroup}
                    ref={(el) => {
                      if (el) el.indeterminate = someInGroup && !allInGroup;
                    }}
                    readOnly
                    className="size-3.5"
                  />
                  {expected} ({group.filter((p) => selected.has(p.persona_id)).length}/{group.length})
                </button>
                <div className="grid gap-1 sm:grid-cols-2">
                  {group.map((p) => (
                    <label
                      key={p.persona_id}
                      className="flex items-start gap-2 rounded px-2 py-1 text-xs hover:bg-accent/30"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(p.persona_id)}
                        onChange={() => toggleOne(p.persona_id)}
                        className="mt-0.5 size-3.5"
                      />
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] truncate">{p.persona_id}</div>
                        <div className="truncate text-muted-foreground">
                          {p.name} · {p.call_type}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PreflightStatus({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="rounded-md border border-status-error-border bg-status-error p-4">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-status-error-text" />
        <div className="space-y-1.5">
          <div className="text-sm font-medium text-status-error-text">
            Run blocked — fix these {errors.length} issue{errors.length === 1 ? '' : 's'} first
          </div>
          <ul className="ml-1 space-y-1 text-xs text-status-error-text">
            {errors.map((e, i) => (
              <li key={i} className="list-disc list-inside">
                {e}
              </li>
            ))}
          </ul>
          <div className="text-xs text-status-error-text/80">
            Open <strong>Variable definitions</strong> above to define unknown variables.
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonaStatePreview({ state }: { state: PersonaState }) {
  const rows: Array<readonly [string, React.ReactNode]> = [
    ['today (frozen)', state.today],
    ['DPD', state.dpd.toString()],
    ['due_date', state.due_date ?? '—'],
    ['next_due_date', state.next_due_date ?? '—'],
    ['statement_date', state.statement_date ?? '—'],
    ['month_end', state.month_end ?? '—'],
    ['latest_allowed_ptp_date', state.latest_allowed_ptp_date ?? '—'],
    ['title', state.title ?? '—'],
    ['first_name', state.first_name ?? '—'],
    ['last_name', state.last_name ?? '—'],
    ['agent_name', state.agent_name ?? '—'],
    ['overdue_amount', state.overdue_amount ?? '—'],
    ['installment_amount', state.installment_amount ?? '—'],
    ['total_outstanding', state.total_outstanding ?? '—'],
  ];
  const extrasEntries = Object.entries(state.extras);
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Derived persona state — what the agent, persona, and judge will all see
      </div>
      <div className="mt-3 grid gap-1 rounded-md border border-border bg-card p-4 text-sm shadow-sm sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <code className="font-mono text-xs text-muted-foreground">{k}</code>
            <span className="font-mono text-xs">{v}</span>
          </div>
        ))}
        {extrasEntries.length > 0 && (
          <>
            <div className="col-span-full pt-2 text-xs uppercase tracking-wide text-muted-foreground">
              extras
            </div>
            {extrasEntries.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3">
                <code className="font-mono text-xs text-muted-foreground">{k}</code>
                <span className={cn('font-mono text-xs', v === '' && 'text-status-error-text')}>
                  {v === '' ? '(not set)' : v}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// New prompt modal — paste OR upload
// ────────────────────────────────────────────────────────────────────

function NewPromptModal({
  cell,
  language,
  onClose,
  onCreated,
}: {
  cell: string;
  language: Language;
  onClose: () => void;
  onCreated: (detail: PromptDetail) => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'body' | 'define'>('body');
  const [tab, setTab] = useState<'paste' | 'upload'>('paste');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);
  const [defs, setDefs] = useState<VariableDefinition[]>([]);
  // Which agent this prompt implements — drives persona pre-selection
  // at run start and tells the judge the call type. '' = not set.
  const [agentType, setAgentType] = useState('');
  const agentTypesQ = useQuery({ queryKey: ['agent-types'], queryFn: api.listAgentTypes });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const personasQ = useQuery({
    queryKey: ['personas', cell, language],
    queryFn: () => api.listPersonas(cell, language),
    retry: 0,
  });
  const personas = personasQ.data?.personas ?? [];

  const unknownNames = useMemo(() => extractUnknownVariables(body), [body]);

  // Sync defs to the current unknownNames (carry forward any partial
  // input the operator already filled).
  useEffect(() => {
    setDefs((prev) => {
      const byName = new Map(prev.map((d) => [d.name, d]));
      return unknownNames.map((n) => byName.get(n) ?? emptyDefinition(n));
    });
  }, [unknownNames]);

  // Sonnet suggestions are an OPT-IN button. We don't auto-fire — the
  // operator screenshot showed the system populating values they didn't
  // ask for. Definitions start empty; if the operator wants AI help,
  // they click the button.
  const autoFillMut = useMutation({
    mutationFn: () =>
      api.autoDefinePrompt(cell, language, {
        body,
        unknown_variable_names: defs
          .filter((d) => d.kind !== 'skip')
          .map((d) => d.name),
      }),
    onSuccess: (resp) => {
      const suggested = new Map(resp.definitions.map((d) => [d.name, d]));
      setDefs((prev) =>
        prev.map((d) => {
          const sug = suggested.get(d.name);
          if (!sug) return d;
          // Don't overwrite a SKIP — operator already declared intent
          if (d.kind === 'skip') return d;
          // Don't overwrite a fixed value the operator typed
          if (d.kind === 'fixed' && d.fixed_value && d.fixed_value.trim()) {
            return d;
          }
          const merged: Record<string, string> = { ...sug.per_persona_values };
          for (const [pid, v] of Object.entries(d.per_persona_values)) {
            if (v && v.trim()) merged[pid] = v;
          }
          return {
            ...d,
            kind: 'per_persona',
            per_persona_values: merged,
          };
        }),
      );
      toast.success('Filled empty cells with AI suggestions — review before Create');
    },
    onError: (e) => toast.error(String(e)),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.createPrompt(cell, language, {
        name: name.trim(),
        body,
        source_filename: sourceFilename ?? undefined,
        variable_definitions: defs,
        agent_type: agentType || null,
      }),
    onSuccess: (detail) => {
      toast.success(`Created ${detail.name} v${detail.version}`);
      qc.invalidateQueries({ queryKey: ['prompts', cell, language] });
      onCreated(detail);
    },
    onError: (e) => toast.error(String(e)),
  });

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setBody(text);
      setSourceFilename(file.name);
      if (!name.trim()) {
        const stem = file.name.replace(/\.md$/i, '').replace(/_v\d+$/i, '');
        setName(stem);
      }
    };
    reader.readAsText(file);
  };

  const nameOk = /^[A-Za-z0-9_.-]{1,128}$/.test(name.trim());
  const allDefsComplete = defs.every((d) => checkComplete(d, personas));
  const canCreate =
    nameOk &&
    body.trim().length > 0 &&
    (unknownNames.length === 0 || allDefsComplete);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm">
      <div className="w-[min(960px,95vw)] max-h-[92vh] overflow-hidden rounded-lg bg-card shadow-md flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-base font-semibold">New prompt</h2>
            <p className="text-xs text-muted-foreground">
              {cell} · {language} — created at v1.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StepIndicator current={step} hasUnknowns={unknownNames.length > 0} />
            <button
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {step === 'body' && (
            <div className="space-y-4 p-5">
              <div className="flex items-center gap-1 rounded-md border border-border bg-muted p-1">
                <TabButton active={tab === 'paste'} onClick={() => setTab('paste')}>
                  Paste markdown
                </TabButton>
                <TabButton active={tab === 'upload'} onClick={() => setTab('upload')}>
                  Upload .md file
                </TabButton>
              </div>

              {tab === 'upload' && (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) onFile(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 py-10 text-sm text-muted-foreground transition-colors hover:bg-accent/30"
                >
                  <Upload className="size-5" />
                  {sourceFilename ? (
                    <span className="font-mono text-xs text-foreground">{sourceFilename}</span>
                  ) : (
                    <>
                      <span>Drop a .md file here, or click to browse</span>
                      <span className="text-xs">UTF-8 markdown</span>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.markdown,text/markdown,text/plain"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onFile(f);
                    }}
                    className="hidden"
                  />
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Prompt name</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Initial_No_Deferment"
                    className={cn(
                      inputCls,
                      !nameOk && name.length > 0 && 'border-status-error-border focus-visible:ring-status-error-border',
                    )}
                  />
                  <span className="text-xs text-muted-foreground">
                    Saved as <code className="font-mono">{name || '<name>'}_v1.md</code>.
                  </span>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Agent type</span>
                  <select
                    value={agentType}
                    onChange={(e) => setAgentType(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— not set —</option>
                    {(agentTypesQ.data ?? []).map((t) => (
                      <option key={t.slug} value={t.slug}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-muted-foreground">
                    Pre-selects matching personas at run start; tells the judge the call type.
                  </span>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    Source filename (optional)
                  </span>
                  <input
                    value={sourceFilename ?? ''}
                    onChange={(e) => setSourceFilename(e.target.value || null)}
                    placeholder="(auto)"
                    className={inputCls}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Prompt body</span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={tab === 'paste' ? 16 : 8}
                  placeholder="# Initial — No deferment&#10;Hello {title} {FirstName}, your overdue is {overdue}…"
                  className="rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <span className="text-xs text-muted-foreground">
                  {body.length.toLocaleString()} chars · {body.split('\n').length} lines
                </span>
              </label>

              <VariableScanSummary body={body} />
            </div>
          )}

          {step === 'define' && (
            <div className="space-y-4 p-5">
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                For each unknown variable below, pick one of three: <strong>Fixed</strong> (same value for all personas), <strong>Per persona</strong> (each persona gets its own), or <strong>Skip</strong> (substitute with whitespace — variable isn't relevant). You can fill values yourself OR click <strong>Auto-fill with AI</strong> to have Sonnet suggest per-persona values.
              </div>

              <div className="flex items-center justify-between rounded-md border border-border bg-card p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">
                  Click to auto-fill empties — values that pass the placeholder check get inserted; existing entries are preserved.
                </div>
                <button
                  onClick={() => autoFillMut.mutate()}
                  disabled={autoFillMut.isPending || defs.filter((d) => d.kind !== 'skip').length === 0}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/40 bg-card px-3 text-xs font-medium text-primary shadow-sm hover:bg-accent disabled:opacity-50"
                >
                  {autoFillMut.isPending ? (
                    <Spinner className="size-3" />
                  ) : (
                    <Sparkles className="size-3.5" />
                  )}
                  {autoFillMut.isPending ? 'Generating…' : 'Auto-fill with AI'}
                </button>
              </div>

              {defs.length === 0 ? (
                <div className="rounded-md border border-success/30 bg-success/10 p-4 text-sm text-success">
                  Nothing to define — every variable in this prompt is glossary-known.
                </div>
              ) : (
                defs.map((d, i) => (
                  <DefinitionEditor
                    key={d.name}
                    variable={d.name}
                    personas={personas}
                    loadingPersonas={personasQ.isLoading}
                    value={d}
                    onChange={(next) => {
                      const copy = [...defs];
                      copy[i] = next;
                      setDefs(copy);
                    }}
                  />
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <div className="text-xs text-muted-foreground">
            {step === 'body'
              ? unknownNames.length > 0
                ? `Step 1 of 2 — ${unknownNames.length} unknown variable${unknownNames.length === 1 ? '' : 's'} will need definitions`
                : 'Step 1 of 1 — no unknowns to define'
              : `Step 2 of 2 — ${defs.filter((d) => !checkComplete(d, personas)).length} still need a value`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent"
            >
              Cancel
            </button>
            {step === 'define' && (
              <button
                onClick={() => setStep('body')}
                className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent"
              >
                Back
              </button>
            )}
            {step === 'body' && unknownNames.length > 0 ? (
              <button
                onClick={() => setStep('define')}
                disabled={!nameOk || !body.trim()}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
              >
                Next: define {unknownNames.length} unknown
              </button>
            ) : (
              <button
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending || !canCreate}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {createMut.isPending ? (
                  <Spinner className="size-3 text-primary-foreground" />
                ) : (
                  <Save className="size-3.5" />
                )}
                Create v1
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({
  current,
  hasUnknowns,
}: {
  current: 'body' | 'define';
  hasUnknowns: boolean;
}) {
  if (!hasUnknowns) return null;
  return (
    <div className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
      <span className={current === 'body' ? 'font-semibold text-foreground' : ''}>
        1. Body
      </span>
      <ChevronRight className="size-3" />
      <span className={current === 'define' ? 'font-semibold text-foreground' : ''}>
        2. Define unknowns
      </span>
    </div>
  );
}

function VariableScanSummary({ body }: { body: string }) {
  if (!body.trim()) return null;
  const tokens = useMemo(() => {
    const found: Array<{ name: string; cls: ReturnType<typeof classifyVariable> }> = [];
    const seen = new Set<string>();
    const re = /\{([A-Za-z][A-Za-z0-9_:\-+]*)\}/g;
    for (const m of body.matchAll(re)) {
      const key = m[1].toLowerCase().replace(/[\s_\-]/g, '');
      if (seen.has(key)) continue;
      seen.add(key);
      found.push({ name: m[1], cls: classifyVariable(m[1]) });
    }
    return found;
  }, [body]);

  if (tokens.length === 0) {
    return (
      <div className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        No <code className="font-mono">{'{variables}'}</code> found in this body yet.
      </div>
    );
  }

  const groups: Record<string, string[]> = { glossary: [], auto_handled: [], unknown: [] };
  for (const t of tokens) groups[t.cls].push(t.name);

  return (
    <div className="rounded-md border border-border bg-muted/10 p-3 text-xs">
      <div className="font-medium text-muted-foreground">
        Detected {tokens.length} variable{tokens.length === 1 ? '' : 's'}
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <Group label="Glossary (auto-resolved)" tone="success" names={groups.glossary} />
        <Group label="Runtime (auto)" tone="muted" names={groups.auto_handled} />
        <Group label="Unknown (need definition)" tone="rose" names={groups.unknown} />
      </div>
    </div>
  );
}

function Group({
  label,
  tone,
  names,
}: {
  label: string;
  tone: 'success' | 'muted' | 'rose';
  names: string[];
}) {
  const tones = {
    success: 'border-success/30 bg-success/5 text-success',
    muted: 'border-border bg-muted text-muted-foreground',
    rose: 'border-status-error-border bg-status-error text-status-error-text',
  };
  return (
    <div className={cn('rounded-md border p-2', tones[tone])}>
      <div className="text-[11px] font-medium uppercase tracking-wide opacity-80">
        {label}
      </div>
      {names.length === 0 ? (
        <div className="mt-1 text-[11px] opacity-60">—</div>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1">
          {names.map((n) => (
            <code key={n} className="rounded bg-card/60 px-1.5 py-0.5 font-mono text-[11px]">
              {`{${n}}`}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

const inputCls = cn(
  'h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
);
